import "@/lib/firebase";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FirebaseProvider } from "@/contexts/FirebaseContext";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense, Component, type ReactNode } from "react";
import { lazyPage } from "@/lib/lazyPage";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { useRoleCheck } from "@/hooks/useRoleCheck";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import OAuthConsent from "./pages/OAuthConsent";
import AgencyPricing from "./pages/AgencyPricing";
import Pricing from "./pages/Pricing";
import Events from "./pages/Events";
import PublicProfile from "./pages/PublicProfile";
import FeedBoard from "@/components/platform/FeedBoard";
import MessagingPage from "./pages/Messages";
import QuickPost from "./pages/QuickPost";
import PaymentReturn from "./pages/PaymentReturn";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import Verification from "./pages/Verification";
import AdminLanding from "./pages/AdminLanding";
import AdminAuth from "./pages/AdminAuth";
import CreatorManagementDashboard from "./pages/CreatorManagementDashboard";
import VenueOversightDashboard from "./pages/VenueOversightDashboard";
import PlatformControlDashboard from "./pages/PlatformControlDashboard";
import Careers from "./pages/Careers";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import GDPRCompliance from "./pages/GDPRCompliance";
import AgencyPartnershipAgreement from "./pages/AgencyPartnershipAgreement";
import BusinessVerificationPolicy from "./pages/BusinessVerificationPolicy";
import PaymentsPolicy from "./pages/PaymentsPolicy";
import CollaborationTerms from "./pages/CollaborationTerms";
import { Checkout, Comparison, Contracts, HelpCenter, Onboarding } from "./pages/PlatformTools";
import MainLayout from "@/components/layouts/MainLayout";
import ScrollToTop from "@/components/ScrollToTop";
import { CartProvider } from "@/hooks/platform/useCart";
import { TransfersProvider } from "@/hooks/platform/useTransfers";
import { MediaPlayerProvider } from "@/hooks/platform/useMediaPlayer";
import MiniPlayer from "@/components/platform/MiniPlayer";
import IncomingCallListener from "@/components/platform/IncomingCallListener";
import WalletAlertsListener from "@/components/platform/WalletAlertsListener";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
    },
  },
});

// Lazy load dashboards for better performance
const LazyCreatorDashboard = lazyPage("creator", () => import("./pages/CreatorDashboard"));
const LazyVenueDashboard = lazyPage("venue", () => import("./pages/VenueDashboard"));
const LazyGrooverDashboard = lazyPage("groover", () => import("./pages/GrooverDashboard"));
const LazyAdminDashboard = lazyPage("admin", () => import("./pages/AdminDashboard"));

// Loading component for lazy loading
const DashboardLoading = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white-600 mx-auto mb-4"></div>
      <p className="text-gray-400">Loading dashboard...</p>
    </div>
  </div>
);

class DashboardImportBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p>This page couldn't load. The app may have updated while it was open.</p>
        <button type="button" className="rounded-md bg-primary px-5 py-2 text-primary-foreground" onClick={() => window.location.reload()}>Reload page</button>
      </div>
    );
    return this.props.children;
  }
}

// Smart redirect component that routes to the correct dashboard based on user_type
const SmartDashboardRedirect = () => {
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    const determineRedirect = async () => {
      // Check demo mode first
      if (isDemoUser()) {
        setRedirectPath("/dashboard/groover/feeds");
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setRedirectPath("/auth");
        return;
      }

      // Agency / platform staff go to the portals their roles protect.
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);
      const roleNames = (roles ?? []).map((r) => r.role as string);
      if (roleNames.includes("admin") || roleNames.includes("platform_control")) {
        setRedirectPath("/admin/platform-control");
        return;
      }
      if (roleNames.includes("creator_management")) {
        setRedirectPath("/admin/creator-management");
        return;
      }
      if (roleNames.includes("venue_oversight")) {
        setRedirectPath("/admin/venue-oversight");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("user_type")
        .eq("id", session.user.id)
        .maybeSingle();

      switch (profile?.user_type) {
        case "creator":
          setRedirectPath("/dashboard/creator/feeds");
          break;
        case "venue":
          setRedirectPath("/dashboard/venue/feeds");
          break;
        default:
          setRedirectPath("/dashboard/groover/feeds");
      }
    };
    determineRedirect();
  }, []);

  if (!redirectPath) return <DashboardLoading />;
  return <Navigate to={redirectPath} replace />;
};

// Check if user is in demo mode
const isDemoUser = () => {
  try {
    const demoUser = localStorage.getItem('demo_user');
    if (demoUser) {
      const parsed = JSON.parse(demoUser);
      return parsed.temporary === true;
    }
  } catch {
    return false;
  }
  return false;
};

// Admin role types that require server-side verification
type AdminRole = 'admin' | 'creator_management' | 'venue_oversight' | 'platform_control';
const ADMIN_ROLES: AdminRole[] = ['admin', 'creator_management', 'venue_oversight', 'platform_control'];

// Helper to check if a role requires server-side verification
const isAdminRole = (role?: string | string[]): role is AdminRole | AdminRole[] => {
  if (Array.isArray(role)) return role.length > 0 && role.every((r) => ADMIN_ROLES.includes(r as AdminRole));
  return !!role && ADMIN_ROLES.includes(role as AdminRole);
};

const DASHBOARD_ROLES = ['groover', 'creator', 'venue'] as const;
type DashboardRole = (typeof DASHBOARD_ROLES)[number];
const isDashboardRole = (role?: string | string[]): role is DashboardRole =>
  typeof role === 'string' && (DASHBOARD_ROLES as readonly string[]).includes(role);

// Protected route wrapper with proper authentication AND server-side role verification
const ProtectedRoute = ({ children, role, allowDemo = false }: { children: React.ReactNode; role?: string | string[]; allowDemo?: boolean }) => {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [roleVerified, setRoleVerified] = useState(false);
  // The signed-in account's own dashboard role, loaded for dashboard routes only.
  const [accountRole, setAccountRole] = useState<DashboardRole | null>(null);
  const [accountRoleLoading, setAccountRoleLoading] = useState(isDashboardRole(role));


  // Use the role check hook for admin roles
  const { hasRole, isLoading: roleHookLoading } = useRoleCheck(
    session,
    isAdminRole(role) ? role : undefined
  );

  useEffect(() => {
    // Check for demo mode
    setIsDemo(isDemoUser());

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setAuthLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Server-side role verification for admin routes
  useEffect(() => {
    if (!isAdminRole(role)) {
      // Non-admin routes don't need server verification
      setRoleVerified(true);
      return;
    }

    if (!session) {
      setRoleVerified(false);
      return;
    }

    // The useRoleCheck hook handles the verification
    if (!roleHookLoading) {
      setRoleVerified(hasRole);
    }
  }, [role, session, hasRole, roleHookLoading]);


  // Dashboard routes: load the account's own role so a creator never renders
  // the Groover dashboard (and vice versa) when a stale link or bookmark is opened.
  useEffect(() => {
    let cancelled = false;
    if (!isDashboardRole(role)) { setAccountRoleLoading(false); return; }
    if (!session) { setAccountRoleLoading(false); return; }
    setAccountRoleLoading(true);
    supabase
      .from('profiles')
      .select('user_type')
      .eq('id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const type = data?.user_type;
        setAccountRole(isDashboardRole(type ?? undefined) ? (type as DashboardRole) : 'groover');
        setAccountRoleLoading(false);
      });
    return () => { cancelled = true; };
  }, [role, session]);

  // Combined loading state
  const isLoading = authLoading || (isAdminRole(role) && roleHookLoading) || (isDashboardRole(role) && !!session && accountRoleLoading);

  if (isLoading) {
    return <DashboardLoading />;
  }


  // Allow access if demo mode is enabled for this route
  if (allowDemo && isDemo) {
    return <>{children}</>;
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  // For admin roles, verify the role was confirmed server-side
  if (isAdminRole(role) && !roleVerified) {
    // User is authenticated but doesn't have the required admin role
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-4">You don't have permission to access this page.</p>
          <a href="/" className="text-purple-500 hover:text-purple-400 underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Route the account to its own dashboard, keeping the view segment where possible.
  if (isDashboardRole(role) && accountRole && accountRole !== role) {
    const view = location.pathname.split('/')[3] || (accountRole === 'venue' ? 'groove-insights' : 'feeds');
    return <Navigate to={`/dashboard/${accountRole}/${view}`} replace />;
  }

  return <>{children}</>;

};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <FirebaseProvider>
      <CartProvider>
      <TransfersProvider>
      <MediaPlayerProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <ScrollToTop />
          <MiniPlayer />
          <IncomingCallListener />
          <WalletAlertsListener />
          <DashboardImportBoundary><Suspense fallback={<DashboardLoading />}>
            <Routes>
              {/* Public Routes with MainLayout */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/events" element={<Events />} />
                <Route path="/post/:id" element={<div className="mx-auto max-w-3xl px-4 pb-16"><FeedBoard title="Post" description="See what's happening on Metro City Groove." composer={false} /></div>} />
                <Route path="/u/:username" element={<PublicProfile />} />
                <Route path="/agency-pricing" element={<AgencyPricing />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/verification" element={<Verification />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/gdpr" element={<GDPRCompliance />} />
                <Route path="/agency-partnership-agreement" element={<AgencyPartnershipAgreement />} />
                <Route path="/business-verification-policy" element={<BusinessVerificationPolicy />} />
                <Route path="/payments-policy" element={<PaymentsPolicy />} />
                <Route path="/collaboration-terms" element={<CollaborationTerms />} />
              </Route>
              <Route path="/landing-page-2" element={<AdminLanding />} />
              <Route path="/admin-auth" element={<AdminAuth />} />
              
              {/* Protected Dashboard Routes - Allow demo access */}
              <Route 
                path="/dashboard/creator/*" 
                element={
                  <ProtectedRoute role="creator" allowDemo>
                    <LazyCreatorDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard/venue/*" 
                element={
                  <ProtectedRoute role="venue" allowDemo>
                    <LazyVenueDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard/groover/*" 
                element={
                  <ProtectedRoute role="groover" allowDemo>
                    <LazyGrooverDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Smart Dashboard Redirect - routes based on user_type */}
              <Route path="/dashboard" element={<SmartDashboardRedirect />} />
              <Route path="/creator-dashboard" element={<Navigate to="/dashboard/creator/feeds" replace />} />
              <Route path="/venue-dashboard" element={<Navigate to="/dashboard/venue/feeds" replace />} />
              <Route path="/groover-dashboard" element={<Navigate to="/dashboard/groover/feeds" replace />} />

              {/* Platform workflows */}
              <Route path="/checkout" element={<ProtectedRoute allowDemo><Checkout /></ProtectedRoute>} />
              <Route path="/payment/return" element={<PaymentReturn />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<ProtectedRoute allowDemo><Onboarding /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute allowDemo><HelpCenter /></ProtectedRoute>} />
              <Route path="/comparison" element={<ProtectedRoute allowDemo><Comparison /></ProtectedRoute>} />
              <Route path="/contracts" element={<ProtectedRoute allowDemo><Contracts /></ProtectedRoute>} />
              
              {/* Communication & Action Routes */}
              <Route 
                path="/messages" 
                element={
                  <ProtectedRoute>
                    <MessagingPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/messaging" 
                element={
                  <ProtectedRoute>
                    <MessagingPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/create/post" 
                element={
                  <ProtectedRoute>
                    <QuickPost />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/quick-post" 
                element={
                  <ProtectedRoute>
                    <QuickPost />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin/dashboard/*" 
                element={
                  <ProtectedRoute role={["admin", "platform_control", "creator_management", "venue_oversight"]}>
                    <LazyAdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/creator-management" 
                element={
                  <ProtectedRoute role={["admin", "platform_control", "creator_management"]}>
                    <CreatorManagementDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/venue-oversight" 
                element={
                  <ProtectedRoute role={["admin", "platform_control", "venue_oversight"]}>
                    <VenueOversightDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/platform-control" 
                element={
                  <ProtectedRoute role={["admin", "platform_control"]}>
                    <PlatformControlDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Catch-all Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense></DashboardImportBoundary>
        </Router>
      </TooltipProvider>
      </MediaPlayerProvider>
      </TransfersProvider>
      </CartProvider>
      </FirebaseProvider>
    </QueryClientProvider>
  );
};

export default App;
